/**
 * Extract message from unknown error.
 * Returns undefined if no message found, so call sites keep their own fallback:
 *   getErrorMessage(err) || t('fallback')
 */
export function getErrorMessage(err: unknown): string | undefined {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null && 'message' in err)
    return String((err as Record<string, unknown>).message);
  return undefined;
}

/** Type guard for Supabase PostgrestError shape (or any error with .code) */
export function isPostgrestError(err: unknown): err is { message: string; code: string; details?: string; hint?: string } {
  return typeof err === 'object' && err !== null && 'code' in err && 'message' in err;
}

/**
 * Reads the JSON body a `supabase.functions.invoke()` failure carries in `.context` —
 * the raw fetch `Response` the client attaches on a non-2xx (`FunctionsHttpError`) but
 * never parses for you. Shared by `getEdgeFunctionErrorDetail` and
 * `getEdgeFunctionErrorBody` below, so the "is this a Response, clone it, parse it"
 * idiom exists exactly once instead of being copied at every call site that needs it.
 *
 * Returns `undefined` when there is no context, it isn't a Response, or the body isn't
 * JSON — callers keep their own generic fallback in that case.
 */
async function readFunctionsErrorContext(err: unknown): Promise<Record<string, unknown> | undefined> {
  if (!err || typeof err !== 'object') return undefined;
  const context = (err as { context?: unknown }).context;
  if (!context || typeof (context as Response).json !== 'function') return undefined;
  try {
    // .clone() so a body already read elsewhere (or read again later) doesn't throw.
    const body = await (context as Response).clone().json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Extract the backend-reported message from a `supabase.functions.invoke()` failure.
 *
 * A non-2xx response comes back as a `FunctionsHttpError` whose `.message` is a generic
 * wrapper ("Edge Function returned a non-2xx status code") — `getErrorMessage()` only sees
 * that wrapper. The real reason lives in the JSON body of `.context`, following this
 * platform's edge-function convention of responding `{ success: false, error: "<message>" }`
 * on failure. Callers that need to tell *why* a call failed (e.g. a Postgres "does not
 * exist" vs. a transient error) read it here instead of guessing from the generic wrapper.
 *
 * Returns `undefined` when there is no context, it isn't a Response, or the body isn't the
 * expected shape — callers keep their own generic fallback in that case.
 */
export async function getEdgeFunctionErrorDetail(err: unknown): Promise<string | undefined> {
  const body = await readFunctionsErrorContext(err);
  const detail = body?.error;
  return typeof detail === 'string' ? detail : undefined;
}

/**
 * Same extraction as `getEdgeFunctionErrorDetail`, but also surfaces the body's `code`
 * field — for a caller (like the WhatsApp send classifier in
 * `whatsapp-error-classifier.ts`) that needs to tell refusals apart by their
 * machine-readable code, not just render the message.
 *
 * Returns `undefined` only when there is nothing usable at all (no `error` and no
 * `code`) — a body that has one but not the other still comes back, so a caller
 * matching on `code` alone isn't starved by a function that omitted the message.
 */
export async function getEdgeFunctionErrorBody(
  err: unknown,
): Promise<{ error?: string; code?: string | number } | undefined> {
  const body = await readFunctionsErrorContext(err);
  if (!body) return undefined;
  const error = typeof body.error === 'string' ? body.error : undefined;
  const code = typeof body.code === 'string' || typeof body.code === 'number' ? body.code : undefined;
  if (error === undefined && code === undefined) return undefined;
  return { error, code };
}
